import { ApolloError, useMutation } from '@apollo/client';
import { Button, Stack } from '@mui/material';
import { Spinner, useSnackbar } from 'components';
import { useEventContext, useGlobalContext, useQuizContext, useQuizSessionContext, useUserContext } from 'contexts';
import { CreateQuizSession, CreateQuizSessionVariables } from 'graphql/mutations/types/CreateQuizSession';
import { createQuizSession } from 'graphql/mutations';
import { CSSProperties } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { NavPages, NavUtils } from 'utils';
import { SessionTypes } from 'consts';

interface Props {
    backgroundStyles: CSSProperties;
    fontStyles: CSSProperties;
    titleStyles: CSSProperties;
    onStartClick: () => void;
    onSinglePlayerStartClick?: () => void;
    onShowParticipantQrCode?: () => void;
}

export const StartScreen = (props: Props) => {
    const { quiz } = useQuizContext();
    const gContext = useGlobalContext();
    const eventContext = useEventContext();
    const session = useQuizSessionContext();
    const user = useUserContext();
    const snackbar = useSnackbar();
    const navigate = useNavigate();
    const { eventAlias } = useParams();
    const { isQuizCreatorOrEventAdmin } = useUserContext();

    const createSessionError = (error: ApolloError) => {
        console.error('Create session error', error);
        snackbar.error('Failed to create session. Please refresh and try again.');
    };

    const onSessionCreated = (data: CreateQuizSession) => {
        navigate(
            NavUtils.generateNavPath(
                NavPages.QuizSessionPresent,
                quiz.id,
                eventContext?.alias,
                data.insert_quiz_master_quiz_session?.returning.at(0)?.key
            )
        );
    };

    const [createSession, { loading }] = useMutation<CreateQuizSession, CreateQuizSessionVariables>(createQuizSession, {
        onError: createSessionError,
        onCompleted: onSessionCreated
    });

    const onStartSimpleLivePaperlessSession = () => {
        const simpleSession: CreateQuizSessionVariables = {
            creatorId: user.id,
            isOpen: true,
            key: crypto.randomUUID().replaceAll('-', ''),
            quizId: quiz.id,
            type: SessionTypes.LivePaperless
        };

        createSession({
            variables: simpleSession
        });
    };

    const onPrintAnswerSheet = () => {
        window.open(NavUtils.generateAbsolutePath(NavPages.PrintAnswerSheet, quiz.id, eventContext?.alias), '_blank');
    };

    const onOpenOverviewPage = () => {
        window.open(NavUtils.generateAbsolutePath(NavPages.QuizSessionOverview, quiz.id, eventContext?.alias, session?.session.key), '_blank');
    };

    return (
        <div style={props.backgroundStyles} className="quiz-title-page-wrapper full-height">
            <Stack direction="column" spacing={2} alignItems="center">
                <h2 className="quiz-title" style={props.titleStyles}>
                    {quiz.name}
                </h2>
                <div style={props.fontStyles}>
                    <p className="quiz-description">{quiz.description}</p>
                    <p className="quiz-genre">{quiz.genreId && <>Genre: {gContext.getGenreName(quiz.genreId)}</>}</p>
                </div>
                {!!props.onSinglePlayerStartClick && (
                    <Button variant="contained" onClick={props.onSinglePlayerStartClick}>
                        Start Single Player Quiz
                    </Button>
                )}
                {!!props.onShowParticipantQrCode && user.isQuizCreatorOrEventAdmin(quiz.id!, eventAlias) && (
                    <Button variant="contained" onClick={props.onShowParticipantQrCode}>
                        Show Player Join Link
                    </Button>
                )}
                {(quiz.publiclyAvailable || user.isQuizCreatorOrEventAdmin(quiz.id!, eventAlias)) && (
                    <>
                        <Button variant="contained" onClick={props.onStartClick}>
                            Start Quiz
                        </Button>
                        <Button variant="contained" onClick={onPrintAnswerSheet}>
                            Print Answer Sheet
                        </Button>
                    </>
                )}
                {isQuizCreatorOrEventAdmin(quiz.id!, eventAlias) && !session && (
                    <>
                        <Button variant="contained" onClick={onStartSimpleLivePaperlessSession}>
                            Start Simple Live Paperless session
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => navigate(NavUtils.generateNavPath(NavPages.EditQuiz, quiz.id, eventContext?.alias))}
                        >
                            Edit Quiz
                        </Button>
                    </>
                )}
                {isQuizCreatorOrEventAdmin(quiz.id!, eventAlias) && session && (
                    <>
                        <Button
                            variant="contained"
                            onClick={onOpenOverviewPage}
                        >
                            Open Overview Page in New Tab
                        </Button>
                    </>
                )}
            </Stack>
            {loading && <Spinner />}
        </div>
    );
};
